const getFirstValue = (...values) =>
  values.find((value) => {
    if (value === undefined || value === null) return false;
    if (typeof value === 'string') return value.trim() !== '';
    return true;
  });

const buildFullName = (...parts) =>
  parts
    .filter((part) => typeof part === 'string' && part.trim())
    .map((part) => part.trim())
    .join(' ');

const normalizeTaskComment = (comment, options = {}) => {
  const source = comment?.Result || comment?.result || comment || {};
  const user = source.user || source.User || {};
  const fallbackUser = options.fallbackUser || {};
  const firstName = getFirstValue(
    source.firstName,
    source.FirstName,
    user.firstName,
    user.FirstName,
    fallbackUser.firstName,
    fallbackUser.FirstName
  );
  const lastName = getFirstValue(
    source.lastName,
    source.LastName,
    user.lastName,
    user.LastName,
    fallbackUser.lastName,
    fallbackUser.LastName
  );
  const username = getFirstValue(
    source.username,
    source.Username,
    user.username,
    user.Username,
    fallbackUser.username,
    fallbackUser.Username
  );
  const userFullName = getFirstValue(
    source.userFullName,
    source.UserFullName,
    source.fullName,
    source.FullName,
    fallbackUser.fullName,
    fallbackUser.FullName,
    buildFullName(firstName, lastName),
    username
  );

  return {
    ...source,
    commentId: getFirstValue(source.commentId, source.CommentId, source.CommentID),
    userId: getFirstValue(
      source.userId,
      source.UserId,
      source.UserID,
      user.userId,
      user.UserID,
      fallbackUser.userId,
      fallbackUser.UserID
    ),
    userFullName,
    username,
    avatar: getFirstValue(
      source.avatar,
      source.Avatar,
      user.avatar,
      user.Avatar,
      fallbackUser.avatar,
      fallbackUser.Avatar
    ),
    text: getFirstValue(source.text, source.Text, options.fallbackText, ''),
    thumbnail: getFirstValue(source.thumbnail, source.Thumbnail, ''),
    mediaThumbnail: getFirstValue(source.mediaThumbnail, source.MediaThumbnail, ''),
    date: getFirstValue(source.date, source.Date, source.sysDate, source.SysDate),
    edited: getFirstValue(source.edited, source.Edited, false),
    seen: getFirstValue(source.seen, source.Seen, options.defaultSeen, false),
  };
};

const normalizeTaskComments = (comments, options) =>
  (Array.isArray(comments) ? comments : [comments])
    .filter(Boolean)
    .map((comment) => normalizeTaskComment(comment, options));

const mergeTaskComments = (currentComments, incomingComments) =>
  currentComments
    .concat(incomingComments)
    .filter((obj, pos, arr) => {
      return (
        arr
          .map((mapObj) => mapObj.commentId)
          .indexOf(obj.commentId) === pos
      );
    })
    .sort((a, b) => {
      return b.commentId - a.commentId;
    });

const initState = {
  loading: false,
  task: {},
  statuses: [],
  assistants: [],
  comments: [],
  documents: [],
  files: [],
  guests: [],
  existingGuests: [],
  latestCommentId: 0,
};

const TaskReducer = function (state = initState, action) {
  switch (action.type) {
    case 'CLEAR_TASK': {
      return {
        ...state,
        task: {},
        loading: false,
      };
    }
    case 'CLEAR_ASSISTANTS': {
      return {
        ...state,
        assistants: [],
        loading: false,
      };
    }
    case 'GET_TASK_PENDING': {
      return {
        ...state,
        loading: true,
      };
    }
    case 'GET_TASK_REJECTED': {
      return {
        ...state,
        loading: false,
      };
    }
    case 'GET_TASK_FULFILLED': {
      return {
        ...state,
        loading: false,
        task: action.payload,
      };
    }
    case 'GET_TASK_COMMENTS_FULFILLED': {
      const comments = normalizeTaskComments(action.payload);
      const commentIds = comments.map(function (comment) {
        return comment.commentId;
      });
      let latestCommentId = commentIds.length > 0
        ? Math.max.apply(Math, commentIds)
        : 0;
      return {
        ...state,
        comments,
        latestCommentId: latestCommentId,
      };
    }
    case 'GET_LATEST_COMMENTS_FULFILLED': {
      const latestComments = normalizeTaskComments(action.payload);
      let comments = mergeTaskComments(state.comments, latestComments);

      let latestCommentId = Math.max.apply(
        Math,
        latestComments.map(function (comment) {
          return comment.commentId;
        })
      );

      return {
        ...state,
        comments: comments,
        latestCommentId: Number.isFinite(latestCommentId)
          ? latestCommentId
          : state.latestCommentId,
      };
    }
    case 'GET_TASK_DOCUMENTS_FULFILLED': {
      return {
        ...state,
        documents: action.payload,
      };
    }
    case 'GET_LATEST_DOCUMENTS_FULFILLED': {
      // Only append documents we don't already have; the latest-documents
      // fetch can overlap with ones already added (e.g. via POST_DOCUMENT or
      // a prior fetch), which would otherwise render the same file twice.
      const existingDocumentIds = new Set(
        state.documents.map((d) => d.documentId)
      );
      const newDocuments = (action.payload ?? []).filter(
        (d) => !existingDocumentIds.has(d.documentId)
      );
      let documents = state.documents.concat(newDocuments).sort((a, b) => {
        return a.documentId - b.documentId;
      });
      return {
        ...state,
        documents: documents,
      };
    }
    case 'GET_TASK_STATUSES_FULFILLED': {
      return {
        ...state,
        statuses: action.payload,
      };
    }
    case 'GET_TASK_GUESTS_FULFILLED': {
      return {
        ...state,
        guests: action.payload,
      };
    }
    case 'GET_ASSISTANTS_FOR_CLIENT_FULFILLED': {
      return {
        ...state,
        assistants: action.payload,
      };
    }

    case 'GET_TASK_AVAILABLE_USERS_FOR_AC_COMPANY_FULFILLED': {
      return {
        ...state,
        assistants: action.payload,
      };
    }

    case 'POST_TASK_STATUS_PENDING': {
      return {
        ...state,
        loading: true,
      };
    }
    case 'POST_TASK_STATUS_REJECTED': {
      return {
        ...state,
        loading: false,
      };
    }
    case 'POST_TASK_STATUS_FULFILLED': {
      let task = state.task;
      task.status = action.payload;

      return {
        ...state,
        task: { ...task },
        loading: false,
      };
    }
    case 'POST_TASK_ASSIGNEE_FULFILLED': {
      let task = state.task;
      task.assignee = action.payload;

      return {
        ...state,
        task: { ...task },
      };
    }
    case 'POST_SAVE_TASK_PENDING': {
      return {
        ...state,
        loading: true,
      };
    }
    case 'POST_SAVE_TASK_REJECTED': {
      return {
        ...state,
        loading: false,
      };
    }
    case 'POST_SAVE_TASK_FULFILLED': {
      let task = state.task;
      task.title = action.payload.title;
      task.description = action.payload.description;

      return {
        ...state,
        task: { ...task },
        loading: false,
      };
    }
    case 'POST_COMMENT_PENDING': {
      return {
        ...state,
        loading: true,
      };
    }
    case 'POST_COMMENT_REJECTED': {
      return {
        ...state,
        loading: false,
      };
    }
    case 'POST_COMMENT_FULFILLED': {
      const savedComment = normalizeTaskComment(action.payload, {
        defaultSeen: true,
        fallbackText: action.meta?.commentText,
        fallbackUser: action.meta?.loggedUser,
      });
      let comments = mergeTaskComments(state.comments, [savedComment]);
      const latestCommentId = Math.max(state.latestCommentId || 0, savedComment.commentId || 0);
      return {
        ...state,
        comments: comments,
        latestCommentId,
        loading: false,
      };
    }
    case 'DELETE_COMMENT_PENDING': {
      return {
        ...state,
        loading: true,
      };
    }
    case 'DELETE_COMMENT_REJECTED': {
      return {
        ...state,
        loading: false,
      };
    }
    case 'DELETE_COMMENT_FULFILLED': {
      let comments = state.comments.filter(
        (comment) => comment.commentId !== action.payload
      );
      return {
        ...state,
        comments: comments,
        loading: false,
      };
    }
    case 'POST_DOCUMENT_PENDING': {
      return {
        ...state,
        loading: true,
      };
    }
    case 'POST_DOCUMENT_REJECTED': {
      return {
        ...state,
        loading: false,
      };
    }
    case 'POST_DOCUMENT_FULFILLED': {
      let document = action.payload;
      // set seen to true when new doc is uploaded
      document.seen = true;

      // Guard against a double-add: the upload POST resolving here can race
      // the SignalR "document added" event, which dispatches getTaskDocuments
      // (a full replace) for the open task. Whichever lands second must not
      // re-add the file, or it shows twice until a manual refresh.
      let documents = state.documents
        .filter((d) => d.documentId !== document.documentId)
        .concat(document)
        .sort((a, b) => {
          return a.documentId - b.documentId;
        });
      return {
        ...state,
        documents: documents,
        loading: false,
      };
    }
    case 'DELETE_DOCUMENT_PENDING': {
      return {
        ...state,
        loading: true,
      };
    }
    case 'DELETE_DOCUMENT_REJECTED': {
      return {
        ...state,
        loading: false,
      };
    }
    case 'DELETE_DOCUMENT_FULFILLED': {
      let documents = state.documents.filter(
        (document) => document.documentId !== action.payload
      );
      return {
        ...state,
        documents: documents,
        loading: false,
      };
    }
    case 'GET_EXISTING_GUESTS': {
      return {
        ...state,
        existingGuests: action.guests,
      };
    }
    case 'ADD_GUEST': {
      return {
        ...state,
        guests: action.guests,
      };
    }
    case 'DELETE_GUEST': {
      return {
        ...state,
        guests: action.guests,
      };
    }

    case 'DELETE_TASK_PENDING': {
      return {
        ...state,
        loading: true,
      };
    }
    case 'DELETE_TASK_REJECTED': {
      return {
        ...state,
        loading: false,
      };
    }

    case 'DELETE_TASK_FULFILLED': {
      return {
        ...state,
        loading: false,
      };
    }

    case 'SET_TASK_COMMENTS_SEEN_PENDING': {
      return {
        ...state,
        loading: false,
      };
    }
    case 'SET_TASK_COMMENTS_SEEN_REJECTED': {
      return {
        ...state,
        loading: false,
      };
    }

    case 'SET_TASK_COMMENTS_SEEN_FULFILLED': {
      return {
        ...state,
        loading: false,
      };
    }

    case 'MARK_TASK_COMMENT_SEEN': {
      let comments = state.comments;
      const commentIndex = comments
        .map(function (c) {
          return c.commentId;
        })
        .indexOf(action.commentId);
      const comment = comments[commentIndex];
      comment.seen = true;
      comments[commentIndex] = comment;

      return {
        ...state,
        loading: false,
        comments,
      };
    }

    case 'UPDATE_TASK_COMMENT_THUMBNAIL': {
      return {
        ...state,
        comments: state.comments.map((c) =>
          c.commentId === action.commentId
            ? { ...c, thumbnail: action.thumbnail }
            : c
        ),
      };
    }

    case 'MARK_TASK_DOCUMENT_SEEN': {
      return {
        ...state,
        loading: false,
        documents: state.documents.map((d) =>
          d.documentId === action.documentId ? { ...d, seen: true } : d
        ),
      };
    }

    default:
      return state;
  }
};

export default TaskReducer;
